import {ReactElement, ReactNode, useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useIsMounted, useModalControls} from '@chekin/guestapp-ui-kit';
import {ERR_INTERNET_DISCONNECTED_MESSAGE} from '@chekin/api';
import {getErrorMessage} from '@chekin/core';
import errorIcon from '../assets/icons/error.svg?no-inline';
import {Modal, ModalButton} from '@guestapp/old-ui-kit';

export type ErrorModalProps = {
  onClose?: () => void;
  open?: boolean;
  message?: string;
  children?: ReactNode | ReactElement | string | null;
  iconSrc?: string;
  iconAlt?: string;
  title?: string | ReactNode;
  description?: string | ReactNode;
  iconProps?: {width: number; height: number};
};

type DisplayErrorDetails = {
  title?: string;
  message?: string | ReactElement;
  description?: string;
};
export function useErrorModal() {
  const {t} = useTranslation();
  const isMounted = useIsMounted();
  const {
    isOpen: isErrorModalOpen,
    openModal: openErrorModal,
    closeModal: closeErrorModal,
  } = useModalControls();

  const [errorTitle, setErrorTitle] = useState(t('service_temporarily_unavailable'));
  const [errorMessage, setErrorMessage] = useState<
    string | ReactElement | ReactElement[]
  >('');
  const [errorDescription, setErrorDescription] = useState<string | ReactElement>('');
  const [isConnectionIssue, setIsConnectionIssue] = useState(false);

  const displayError = useCallback(
    // TODO Any type. Should be fixed
    // eslint-disable-next-line
    (error: any = {}, {title, message, description}: DisplayErrorDetails = {}) => {
      const isConnectionIssue = error.name === ERR_INTERNET_DISCONNECTED_MESSAGE;
      setIsConnectionIssue(isConnectionIssue);
      if (!isMounted.current) {
        return;
      }

      const errorMessage = message || getErrorMessage(error);
      const errorTitle =
        title || error.status <= 500
          ? t(title ?? 'error')
          : isConnectionIssue
          ? t('no_internet_connection')
          : null;
      const errorDescription = isConnectionIssue
        ? t('no_internet_description')
        : description || '';

      if (errorMessage) setErrorMessage(message || errorMessage);
      if (errorDescription) setErrorDescription(errorDescription);
      if (errorTitle) setErrorTitle(errorTitle);
      openErrorModal();
    },
    [isMounted, openErrorModal, t, setIsConnectionIssue],
  );

  const ErrorModal = useCallback(
    ({
      open,
      onClose,
      title,
      message,
      description,
      children,
      iconSrc,
      iconAlt,
      iconProps,
    }: ErrorModalProps) => {
      const displayedTitle = title || errorTitle;
      const displayedMessage = message || errorMessage;
      const displayedDescription = description || errorDescription;

      const getTextContent = () => {
        if (displayedMessage) {
          return (
            <span>
              {displayedMessage}
              <p />
              {displayedDescription}
            </span>
          );
        }

        return (
          <span>
            {t('we_found_error')}
            <p />
            {t('try_again_or_contact')}
          </span>
        );
      };

      return (
        <Modal
          open={open !== undefined ? open : isErrorModalOpen}
          title={displayedTitle}
          text={getTextContent()}
          iconSrc={iconSrc || errorIcon}
          iconAlt={iconAlt || 'Exclamation error mark'}
          iconProps={iconProps || {width: 180, height: 180}}
          onClose={closeErrorModal}
          withCloseButton
          isConnectionIssue={isConnectionIssue}
          className="error-modal kiosko:min-w-[800px]"
        >
          {children || (
            <ModalButton
              label={t('ok')}
              onClick={onClose || closeErrorModal}
              className="kiosko:w-full kiosko:rounded-[56px] kiosko:h-[114px] kiosko:text-kiosko-m kiosko:mt-14"
            />
          )}
        </Modal>
      );
    },
    [
      isErrorModalOpen,
      errorDescription,
      closeErrorModal,
      errorMessage,
      errorTitle,
      t,
      isConnectionIssue,
    ],
  );

  return {
    displayError,
    closeErrorModal,
    openErrorModal,
    ErrorModal,
  };
}
